import React, { useContext } from 'react';
import { Container, Grid2, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MailOutline from '@mui/icons-material/MailOutline';
import Drafts from '@mui/icons-material/Drafts';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ExpensesUpload from '../Components/Expense/ExpensesUpload';
import ExpenseCard from '../Components/Expense/ExpenseCard';
import { AdminAuthContext } from '../../Admin/Contexts/AdminAuthContext';

const styles = (theme) => ({
    ContainerRoot: {
        [theme.breakpoints.down('md')]: {
            paddingLeft: '16px',
            paddingRight: '16px',
        },
        paddingTop: '2.5rem',
        paddingBottom: '5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '40px',
    },
    header: {
        fontWeight: '700',
        margin: 0,
        color: '#336195',
        marginBottom: 24,
    },
    description: {
        fontSize: '1rem',
    },
});

const Expenses = (props) => {
    const { classes } = props;
    const { isExpenseSystemAdministratorUser, isExpenseAdminUser, isClinicianReimEntryUser } =
        useContext(AdminAuthContext);
    const isViewOnly = !(
        isExpenseSystemAdministratorUser ||
        isExpenseAdminUser ||
        isClinicianReimEntryUser
    );
    const flags = useFlags();
    const {
        ltoeWebExpensesEnableExpenseNewCheckRequest: isVendorPaymentEnabled,
        ltoeWebExpensesEnablePendingCcTextUpdate: isPendingCCNewText,
        ltoeWebExpensesEnableCcReviewTextUpdate: isCCReviewNewText,
        ltoeWebExpensesEnableNewCreditCardReview: isNewCreditCardReviewEnabled,
        ltoeWebExpensesEnableVaultChargesReview: isVaultChargesReviewEnabled,
        ltoeWebExpensesEnablePreCodedExpenseLogic: isPreCodedExpenseLogicEnabled,
        ltoeWebExpensesEnableFileUpload: isFileUploadEnabled,
    } = flags;

    return (
        <Container maxWidth="md" className={classes.ContainerRoot}>
            <Grid2 container direction="column" spacing={6}>
                <Grid2 size={12}>
                    <Typography
                        variant="h3"
                        component="h3"
                        className={classes.header}
                        align="center"
                    >
                        Expense Management
                    </Typography>
                    <Typography variant="body2" align="center" className={classes.description}>
                        Create, review, and upload expense reports - all in one convenient location!
                    </Typography>
                </Grid2>
                {!isViewOnly && (
                    <Grid2 size={12}>
                        <Typography variant="h5" component="h5" className={classes.header}>
                            Create An Expense
                        </Typography>
                        <Grid2 container direction="column" spacing={2}>
                            <ExpenseCard
                                icon={<LocalAtmIcon color="primary" />}
                                label="New Clinician Reimbursement"
                                path="/expenses/clinician-reimbursement"
                            />
                            {isVendorPaymentEnabled && !isClinicianReimEntryUser && (
                                <ExpenseCard
                                    icon={<MailOutline color="primary" />}
                                    label="New Payment Request"
                                    path="/expenses/payment-request"
                                />
                            )}
                            {isPreCodedExpenseLogicEnabled && !isClinicianReimEntryUser && (
                                <ExpenseCard
                                    icon={<CreditCardIcon color="primary" />}
                                    label="New Pending CC"
                                    path="/expenses/pending-cc"
                                />
                            )}
                        </Grid2>
                    </Grid2>
                )}

                <Grid2 size={12}>
                    <Typography variant="h5" component="h5" className={classes.header}>
                        Review Expenses
                    </Typography>
                    <Grid2 container direction="column" spacing={2}>
                        <ExpenseCard
                            icon={<FindInPageIcon color="primary" />}
                            label={
                                isCCReviewNewText
                                    ? 'Clinician Reimbursements'
                                    : 'CC/CR Expense Review'
                            }
                            path="/expenses/review"
                        />
                        {isVendorPaymentEnabled && !isClinicianReimEntryUser && (
                            <ExpenseCard
                                icon={<Drafts color="primary" />}
                                label="Payments"
                                path="/expenses/payment-request/review"
                            />
                        )}

                        {isPreCodedExpenseLogicEnabled && !isClinicianReimEntryUser && (
                            <ExpenseCard
                                icon={<CreditCardIcon color="primary" />}
                                label={
                                    isPendingCCNewText
                                        ? 'Pre-Coded Credit Card Transactions'
                                        : 'Pending CC Review'
                                }
                                path="/expenses/pending-cc/review"
                            />
                        )}

                        {isNewCreditCardReviewEnabled && !isClinicianReimEntryUser && (
                            <ExpenseCard
                                icon={<CreditCardIcon color="primary" />}
                                label="Credit Card Transactions"
                                path="/expenses/credit-card/review"
                            />
                        )}
                        {isVaultChargesReviewEnabled && !isClinicianReimEntryUser && (
                            <ExpenseCard
                                icon={<LocalHospitalIcon color="primary" />}
                                label="Vault Charges"
                                path="/expenses/vault/review"
                            />
                        )}
                    </Grid2>
                </Grid2>
                {isFileUploadEnabled &&
                    (isExpenseSystemAdministratorUser || isExpenseAdminUser) &&
                    !isClinicianReimEntryUser && (
                        <Grid2 size={12}>
                            <Typography variant="h5" component="h5" className={classes.header}>
                                CSV File Upload
                            </Typography>
                            <ExpensesUpload />
                        </Grid2>
                    )}
            </Grid2>
        </Container>
    );
};

export default withStyles(styles)(Expenses);
